
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { MessageCircle } from 'lucide-react';
import { CommonObjection } from './types';

interface ObjectionsProps {
  objections: CommonObjection[];
  faithGroup?: string;
}

export const Objections: React.FC<ObjectionsProps> = ({ objections, faithGroup }) => {
  // Validate input first
  if (!objections || !Array.isArray(objections)) {
    console.log("No valid objections array to display:", objections);
    return null;
  }
  
  // Enhanced processing of objections to ensure we have at least 2-3
  const processedObjections = objections
    .filter(obj => obj && obj.objection && obj.rebuttal)
    .map(obj => ({
      objection: obj.objection.trim(),
      rebuttal: obj.rebuttal.trim()
    }));
  
  // Add default objections if we don't have enough
  if (processedObjections.length < 2) {
    console.log("Not enough objections, adding default ones");
    
    // Only add these if we don't already have enough
    const defaultObjections = [
      {
        objection: "The Quran states that Jesus was not crucified, so how can Christians claim he died for our sins?",
        rebuttal: "While the Quran does deny the crucifixion, the historical evidence for Jesus' death by crucifixion is overwhelming, supported by Roman and Jewish historians like Tacitus and Josephus. The Gospel accounts, written much closer to the events, provide detailed eyewitness testimony of the crucifixion. The resurrection that followed is the foundation of Christian faith and has transformed countless lives throughout history."
      },
      {
        objection: "Islam teaches that Jesus was just a prophet, not God. How can a man be God?",
        rebuttal: "Christianity doesn't teach that a man became God, but rather that God became man in Jesus Christ. The concept of the Trinity explains that God is one in essence but exists in three persons. Jesus' divine claims (John 8:58, 10:30), his acceptance of worship, and his ability to forgive sins all point to his divine nature. His resurrection validates these claims in a way that mere prophetic status cannot explain."
      },
      {
        objection: "The Bible has been corrupted over time, so we can't trust what it says about Jesus.",
        rebuttal: "Textual scholarship has firmly established the reliability of the biblical texts. With over 5,800 Greek manuscripts of the New Testament, scholars can reconstruct the original text with 99.5% accuracy. The Dead Sea Scrolls confirmed the reliability of Old Testament transmission. The Bible's prophecies about Jesus, written centuries before his birth, demonstrate divine inspiration and verify that its message about Jesus remains intact."
      }
    ];
    
    // Only add enough default objections to reach at least 2
    const neededCount = Math.max(0, 2 - processedObjections.length);
    for (let i = 0; i < neededCount; i++) {
      if (i < defaultObjections.length) {
        processedObjections.push(defaultObjections[i]);
      }
    }
  }
  
  // If still no valid objections after processing, don't render anything
  if (processedObjections.length === 0) {
    console.log("No valid objections after processing");
    return null;
  }

  // Generate an appropriate title based on the faith group
  const title = faithGroup 
    ? `Common ${faithGroup.charAt(0).toUpperCase() + faithGroup.slice(1)} Objections & Rebuttals`
    : "Common Objections & Rebuttals";

  return (
    <Card className="border-heaven-gold/10">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-heaven-gold text-lg">
          <MessageCircle className="h-5 w-5" />
          <span>{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {processedObjections.map((objection, i) => (
            <div key={i} className="mb-4 text-left">
              <p className="font-semibold text-gray-800">
                Objection {i + 1}: {objection.objection}
              </p>
              <p className="text-gray-700 pl-4 border-l-2 border-heaven-gold/30 mt-2">
                <span className="font-medium text-heaven-gold">Response:</span>{' '}
                {objection.rebuttal}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
